import { FC, FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'
import { Col } from 'antd'
import { LatestContractDtoFragment, Currency, CareerPathEvaluation, CareerPathDto, Salary, Maybe } from '../../../types'
import { TopicCard } from '../../../components/custom_components/TopicCard'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { CareerPathPopover } from '../../../components/custom_components/CarrerPathPopover/CareerPathPopover'
import PerText from '../../../components/custom_components/Text'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { reduceExecutedPayments, SlimExecutedPayment } from './helpers'
import { PerCollapse } from '../../../components/custom_components/Collapse'
import { filterCareerPathReferencesByLevel } from './careerPath/careerpath-util'
import useHasCountryNationalContractReferenceLevel from '../../../graphql/countrySettings/hooks/useHasCountryNationalContractReferenceLevel'
import { LoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors'
import { round } from '../../../shared/numberUtil'
import { ProrataTooltip } from '../appraisal-flow/shared/ProrataTooltip'
import { ProrataPartial } from '../appraisal-flow/shared/targetBonusCalculation-util'
import { isDefined } from '../../../types.helper'
import { retrieveCurrencyConversionRate } from '../../../shared/currencyConversionUtil'
import { useCurrency } from '../../../context/currency-context'

interface Props {
  currentContract: LatestContractDtoFragment
  employeeTimezone: string
  referenceYear: number
  careerPathEvaluation?: CareerPathEvaluation | null
  careerPathData: CareerPathDto[]
  newBonus: {
    effectiveBonusAmount: number
    annualAmount: number
    prorata: number
    currency: Currency
    employeeTimezone: string
    prorataPartials: ProrataPartial[]
  }
  isPreparation?: boolean
  newTargetsAllowed: boolean
}

interface ContractItemProps {
  label: string
  labelValue?: number
  span?: number
}

export const CurrentContractOverview: FC<Props> = ({
  currentContract,
  employeeTimezone,
  referenceYear,
  newBonus,
  careerPathEvaluation,
  careerPathData,
  newTargetsAllowed,
  isPreparation = false,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { formatCurrency } = useAmountAndCurrency(user.locale)
  const { formatDateShort } = useDateTime({ locale: user.locale, timezone: employeeTimezone })
  const { currencyConversionData } = useCurrency()
  const {
    data: hasNationalContractReference,
    loading,
    error,
  } = useHasCountryNationalContractReferenceLevel(currentContract.legalEntity.country)
  if (
    !hasNationalContractReference ||
    hasNationalContractReference.hasCountryNationalContractReferenceLevel === undefined ||
    loading ||
    error
  ) {
    return <LoadingAndErrors hasNoData={hasNationalContractReference === undefined} isLoading={loading} error={error} />
  }

  const executedPayments: SlimExecutedPayment[] = currentContract.executedPayments ? currentContract.executedPayments : []
  const payments = reduceExecutedPayments(executedPayments, referenceYear)
  const hasNationalContract = hasNationalContractReference.hasCountryNationalContractReferenceLevel

  const formattedCurrency = (
    currencyObject?: Maybe<{ __typename?: 'Salary' | undefined } & Pick<Salary, 'currency' | 'amount' | 'isTentative'>>,
  ) => {
    if (currencyObject && currencyObject.amount && currencyObject.currency) {
      return formatCurrency(currencyObject.amount, currencyObject.currency, true)
    } else {
      return ''
    }
  }

  const compactProrataInfo =
    (currentContract.isMonthlySalaryDisplayed && !!currentContract.annualSalary) ||
    !!payments.bonusPrepayment ||
    !!payments.valuableHours ||
    !!payments.valuableSalary ||
    !!payments.carAllowance ||
    !!currentContract.companyCar

  const renderPayments = (payments: { amount: number; currency: Currency }[]) => (
    <div style={{ whiteSpace: 'pre-line' }}>{payments.map(({ amount, currency }) => formatCurrency(amount, currency)).join('\n')}</div>
  )

  const ContractItem: FunctionComponent<ContractItemProps> = ({ label, labelValue, span = 8, children }) => {
    return (
      <Col span={span}>
        <PerText appearance="label-small" color="secondary" mb="xxxs">
          {labelValue ? t(`appraisalMeeting.contract.${label}`, { year: labelValue }) : t(`appraisalMeeting.contract.${label}`)}
        </PerText>
        <PerText mb="s">{children}</PerText>
      </Col>
    )
  }

  const references = !!currentContract.employeeLevel
    ? filterCareerPathReferencesByLevel(careerPathData, currentContract.employeeLevel.level)
    : null

  const newBonusAnnualAmountInOriginalCurrency = (): number => {
    if (!currentContract.targetBonus || currentContract.targetBonus.currency === currentContract.currencyForPayments) {
      return newBonus.annualAmount
    }
    const conversionRate: number | undefined = retrieveCurrencyConversionRate(
      currentContract.currencyForPayments,
      currentContract.targetBonus.currency,
      currencyConversionData,
    )
    if (isDefined(conversionRate)) {
      return Number((newBonus.annualAmount * conversionRate!).toFixed(2))
    } else {
      return newBonus.annualAmount
    }
  }

  const bonusAmountInOriginalCurrency = newBonusAnnualAmountInOriginalCurrency()
  const hasProrataBreakdown = newBonus.prorata !== newBonus.annualAmount
  const hasFlexibleBonusAmount = newBonus.prorata !== newBonus.effectiveBonusAmount
  const showBonusDetails = hasProrataBreakdown || hasFlexibleBonusAmount

  return (
    <>
      <PerFlexRow>
        <Col span={12}>
          <TopicCard heading="appraisalMeeting.contract.general.title">
            <PerFlexRow className="fullWidth">
              {currentContract.nationalContractLevel && hasNationalContract && (
                <ContractItem label="nationalReferenceLevel">
                  {
                    <>
                      {t(`employee.nationalContract.shortType.${currentContract.nationalContractLevel.type}`)} -{' '}
                      {t(`employee.nationalContract.level.${currentContract.nationalContractLevel.level}`)}
                    </>
                  }
                </ContractItem>
              )}
              <ContractItem label="employeeLevel">
                {t(`employee.contract.level.${currentContract.employeeLevel && currentContract.employeeLevel.level}`)}
              </ContractItem>
              {currentContract.leaveEntitlement && (
                <ContractItem label="leaveEntitlement">
                  {`${currentContract.leaveEntitlement.amount} ${t(`appraisalMeeting.contract.leaveEntitlementMeasurement`)}`}
                </ContractItem>
              )}
              <ContractItem label="contractType">
                {t(`contract.typeOfContract.${currentContract.contractType.typeOfContract}`)}
              </ContractItem>
              {currentContract.validTo && (
                <>
                  <ContractItem label="endOfContract">{formatDateShort(currentContract.validTo)}</ContractItem>
                  <ContractItem label="cancellationType">
                    {currentContract.cancellationType ? t(`contract.cancellationType.${currentContract.cancellationType}`) : '-'}
                  </ContractItem>
                </>
              )}
              {isPreparation && !!careerPathEvaluation && !!references && (
                <ContractItem label="careerPath">
                  <PerFlexRow>
                    <PerText mr="xxs">{t(`appraisalMeeting.contract.careerPath`)}</PerText>{' '}
                    <CareerPathPopover
                      careerPath={careerPathEvaluation}
                      bonusReference={references.bonus}
                      salaryReference={references.salary}
                      currency={currentContract.currencyForPayments}
                    />
                  </PerFlexRow>
                </ContractItem>
              )}
              {!isPreparation && !!careerPathEvaluation && !!references && (
                <Col span={8}>
                  <div className="per-collapse-header">
                    <PerCollapse
                      panels={[
                        {
                          headingElement: (
                            <PerText appearance="label-small" color="secondary" mb="none">
                              {t(`appraisalMeeting.contract.careerPath`)}
                            </PerText>
                          ),
                          content: (
                            <PerFlexRow width="100%">
                              <PerText mr="xxs">{t(`appraisalMeeting.contract.careerPath`)}</PerText>{' '}
                              <CareerPathPopover
                                careerPath={careerPathEvaluation}
                                bonusReference={references.bonus}
                                salaryReference={references.salary}
                                currency={currentContract.currencyForPayments}
                              />
                            </PerFlexRow>
                          ),
                        },
                      ]}
                      stripMargin
                      iconSize={8}
                    />
                  </div>
                </Col>
              )}
            </PerFlexRow>
          </TopicCard>
        </Col>
        <Col span={12}>
          <TopicCard heading="appraisalMeeting.contract.financial.title">
            <PerFlexRow className="fullWidth">
              <ContractItem label="annualSalary">{formattedCurrency(currentContract.annualSalary)}</ContractItem>
              {currentContract.isMonthlySalaryDisplayed && !!currentContract.annualSalary && (
                <ContractItem label="monthlySalary">
                  {formatCurrency(
                    round(currentContract.annualSalary.amount / currentContract.monthlySalaryMonthsInYear, 2),
                    currentContract.annualSalary.currency,
                  )}
                </ContractItem>
              )}
              {(currentContract.targetBonus || newBonus) && (
                <ContractItem
                  label="annualBonus"
                  labelValue={referenceYear}
                  span={showBonusDetails && !compactProrataInfo ? 16 : undefined}
                >
                  {!newTargetsAllowed && currentContract.targetBonus && formatCurrency(0, currentContract.targetBonus.currency, true)}
                  {newTargetsAllowed &&
                    (showBonusDetails ? (
                      <PerFlexRow direction="column">
                        <PerFlexRow direction="row" mb="xs">
                          <PerText mr="xs">{formatCurrency(newBonus.effectiveBonusAmount, newBonus.currency, true)}</PerText>
                        </PerFlexRow>

                        {hasFlexibleBonusAmount ? (
                          <PerFlexRow direction={compactProrataInfo ? 'column' : 'row'} mb="xxs">
                            <PerText fontSize="xs" color="secondary" mr="xxxs">
                              {t('appraisalMeeting.targetBonusOverview.flexibleBonusAmount')}
                            </PerText>
                            <PerText fontSize="xs" color="secondary" mr="xxs">
                              {formatCurrency(newBonus.effectiveBonusAmount, newBonus.currency, true)}
                            </PerText>
                          </PerFlexRow>
                        ) : (
                          <>
                            <PerFlexRow direction={compactProrataInfo ? 'column' : 'row'} mb="xxs">
                              <PerText fontSize="xs" color="secondary" mr="xxxs">
                                {t('appraisalMeeting.targetBonusOverview.annualAmount')}
                              </PerText>
                              <PerText fontSize="xs" color="secondary" mr="xxs">
                                {formatCurrency(newBonus.annualAmount, newBonus.currency, true)}
                              </PerText>
                              {!!currentContract.targetBonus &&
                                currentContract.targetBonus.currency !== currentContract.currencyForPayments &&
                                !!newBonus && (
                                  <PerText fontSize="xs" color="secondary">
                                    {'(' + formatCurrency(bonusAmountInOriginalCurrency, currentContract.targetBonus.currency, true) + ')'}
                                  </PerText>
                                )}
                            </PerFlexRow>

                            <PerFlexRow direction="row">
                              <PerFlexRow direction={compactProrataInfo ? 'column' : 'row'} mr="xxs">
                                <PerText fontSize="xs" color="secondary" mr="xxxs">
                                  {t('appraisalMeeting.targetBonusOverview.proratedAmount')}
                                </PerText>
                                <PerText fontSize="xs" color="secondary">
                                  {formatCurrency(newBonus.prorata, newBonus.currency, true)}
                                </PerText>
                              </PerFlexRow>
                              <PerFlexRow direction="column" justify="end" mr="xxs">
                                <ProrataTooltip
                                  prorataPartials={newBonus.prorataPartials}
                                  currency={newBonus.currency}
                                  employeeTimezone={newBonus.employeeTimezone}
                                />
                              </PerFlexRow>
                            </PerFlexRow>
                          </>
                        )}
                      </PerFlexRow>
                    ) : (
                      <>
                        <PerText mr="s">{formatCurrency(newBonus.effectiveBonusAmount, newBonus.currency, true)}</PerText>
                        {currentContract.targetBonus && newBonus.currency !== currentContract.targetBonus.currency && (
                          <PerText mt="xxxs" fontSize="s">
                            {'(' + formatCurrency(bonusAmountInOriginalCurrency, currentContract.targetBonus.currency, true) + ')'}
                          </PerText>
                        )}
                      </>
                    ))}
                </ContractItem>
              )}
              {payments.bonusPrepayment && <ContractItem label="bonusPrepayment">{renderPayments(payments.bonusPrepayment)}</ContractItem>}
              {payments.valuableHours && <ContractItem label="valuableHours">{renderPayments(payments.valuableHours)}</ContractItem>}
              {payments.valuableSalary && (
                <ContractItem label="variableSalaryHours">{renderPayments(payments.valuableSalary)}</ContractItem>
              )}
              {payments.carAllowance && <ContractItem label="companyCarAllowance">{renderPayments(payments.carAllowance)}</ContractItem>}
              {currentContract.companyCar && (
                <ContractItem label="companyCar">
                  <PerFlexRow align="middle">
                    <PerText>{t('global.yes')}</PerText>
                    {!!currentContract.companyCar.deduction && currentContract.companyCar.deduction !== 0 && (
                      <>
                        <PerText mx="xxs" color="secondary">
                          |
                        </PerText>
                        <div style={{ marginTop: '-2px' }}>
                          <PerText appearance="label-small" color="default">
                            {`${t('appraisalMeeting.contract.companyCarDeduction')}:`}
                          </PerText>
                          <PerText>
                            {`- ${formatCurrency(currentContract.companyCar.deduction, currentContract.companyCar.currency, true)}`}
                          </PerText>
                        </div>
                      </>
                    )}
                  </PerFlexRow>
                </ContractItem>
              )}
            </PerFlexRow>
          </TopicCard>
        </Col>
      </PerFlexRow>
    </>
  )
}
